/**
 * Persisted guideline_review_version helpers for Medical Necessity analytics.
 *
 * Intentional asymmetry with `review_session_id`: the in-memory review session
 * resets on every screen mount, while these versions live in sessionStorage for
 * the tab so Edit / Override across remounts of the same order keep a continuous
 * version counter. Keys for other orders are pruned on mount to bound growth.
 *
 * @module medicalNecessityAnalytics/guidelineReviewVersion
 */

const REVIEW_VERSION_STORAGE_PREFIX = "mn_guideline_review_version:";

/**
 * Storage key for a persisted guideline review version.
 *
 * @param analysisId - Stable analysis ID (order ID)
 * @param guidelineId - Stable guideline ID
 * @returns sessionStorage key
 */
const reviewVersionKey = (analysisId: string, guidelineId: string): string =>
  `${REVIEW_VERSION_STORAGE_PREFIX}${analysisId}:${guidelineId}`;

/**
 * Reads the current guideline_review_version (defaults to 1).
 *
 * @param analysisId - Stable analysis ID
 * @param guidelineId - Stable guideline ID
 * @returns Current review version (>= 1)
 */
export const getGuidelineReviewVersion = (
  analysisId: string,
  guidelineId: string,
): number => {
  try {
    const raw = sessionStorage.getItem(
      reviewVersionKey(analysisId, guidelineId),
    );
    const parsed = raw ? Number.parseInt(raw, 10) : 1;
    return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
  } catch {
    return 1;
  }
};

/**
 * Persists a guideline review version.
 *
 * @param analysisId - Stable analysis ID
 * @param guidelineId - Stable guideline ID
 * @param version - Version to store
 */
export const setGuidelineReviewVersion = (
  analysisId: string,
  guidelineId: string,
  version: number,
): void => {
  try {
    sessionStorage.setItem(
      reviewVersionKey(analysisId, guidelineId),
      String(Math.max(1, version)),
    );
  } catch {
    // sessionStorage may be unavailable; ignore
  }
};

/**
 * Increments and returns the next guideline_review_version for Edit / Override.
 *
 * @param analysisId - Stable analysis ID
 * @param guidelineId - Stable guideline ID
 * @returns The new (incremented) version
 */
export const bumpGuidelineReviewVersion = (
  analysisId: string,
  guidelineId: string,
): number => {
  const next = getGuidelineReviewVersion(analysisId, guidelineId) + 1;
  setGuidelineReviewVersion(analysisId, guidelineId, next);
  return next;
};

/**
 * Removes review-version keys for every analysis except the current one.
 *
 * Keeps the active order's versions for the tab while preventing unbounded
 * growth as the reviewer visits many orders in one session.
 *
 * @param analysisId - Analysis ID whose keys should be retained
 */
export const pruneGuidelineReviewVersionsExcept = (
  analysisId: string,
): void => {
  if (!analysisId) return;
  try {
    const keepPrefix = `${REVIEW_VERSION_STORAGE_PREFIX}${analysisId}:`;
    const toRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (
        key &&
        key.startsWith(REVIEW_VERSION_STORAGE_PREFIX) &&
        !key.startsWith(keepPrefix)
      ) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // sessionStorage may be unavailable; ignore
  }
};
